/**
 * The fold, in TypeScript — the mirror of `ondo.witness_merkle_root()` and
 * `ondo.witness_merkle_path()` in `20261009090000_the_record_calls_a_witness.sql`.
 *
 * WHY IT EXISTS TWICE. The database defines the tree, because the database is
 * where the hour closes. A reader checking a proof must not have to trust the
 * database's arithmetic — that is the whole point of a witness — so the fold is
 * written again here, over an injected hash, and `merkle.test.ts` pins both
 * against the same vectors `rls.sh` pins the SQL against. If the two ever
 * disagree, the vectors say which moved.
 *
 * THE RULES, so a reader can check them against the SQL by eye:
 *
 *   1. A single leaf is paired with itself: the root of [x] is H(x ‖ x).
 *   2. An odd level repeats its last element before pairing.
 *   3. Pairs are hashed left ‖ right, in order, with no prefix byte and no
 *      length — the salts inside every leaf are what rule out a second
 *      preimage here, not a domain separator.
 *   4. The path for a leaf lists, bottom up, the SIBLING at each level and
 *      which side the sibling sits on.
 *
 * THE HASH IS AN ARGUMENT, because this module runs in three places with three
 * different SHA-256s: `node:crypto` on the API and in these tests,
 * `crypto.subtle` in the browser, and whatever the phone has. A module that
 * imported one of them would run in one of the three.
 */
export function hexToBytes(hex) {
    if (hex.length % 2 !== 0 || !/^[0-9a-f]*$/i.test(hex)) {
        throw new Error(`not hex: ${hex.slice(0, 16)}`);
    }
    const out = new Uint8Array(hex.length / 2);
    for (let i = 0; i < out.length; i += 1) {
        out[i] = Number.parseInt(hex.slice(i * 2, i * 2 + 2), 16);
    }
    return out;
}
export function bytesToHex(bytes) {
    let out = "";
    for (const byte of bytes)
        out += byte.toString(16).padStart(2, "0");
    return out;
}
export function concatBytes(...parts) {
    const out = new Uint8Array(parts.reduce((n, part) => n + part.length, 0));
    let at = 0;
    for (const part of parts) {
        out.set(part, at);
        at += part.length;
    }
    return out;
}
export function bytesEqual(a, b) {
    if (a.length !== b.length)
        return false;
    let diff = 0;
    for (let i = 0; i < a.length; i += 1)
        diff |= (a[i] ?? 0) ^ (b[i] ?? 0);
    return diff === 0;
}
/** Rule 1 and rule 2 applied to one level. */
function padded(level) {
    if (level.length === 1)
        return [level[0], level[0]];
    if (level.length % 2 === 1)
        return [...level, level[level.length - 1]];
    return level;
}
async function fold(level, sha256) {
    const next = [];
    for (let i = 0; i < level.length; i += 2) {
        next.push(await sha256(concatBytes(level[i], level[i + 1])));
    }
    return next;
}
/** The root over `leaves`, in order. Throws on no leaves, as the SQL does. */
export async function merkleRoot(leaves, sha256) {
    if (leaves.length === 0)
        throw new Error("a tree needs at least one leaf");
    let level = padded(leaves);
    while (level.length > 1) {
        level = await fold(padded(level), sha256);
    }
    return level[0];
}
/** The siblings from the leaf at `index` (1-based) up to the root. Rule 4. */
export async function merklePath(leaves, index, sha256) {
    if (!Number.isInteger(index) || index < 1 || index > leaves.length) {
        throw new Error("no such leaf");
    }
    const path = [];
    let level = padded(leaves);
    let at = index;
    while (level.length > 1) {
        level = padded(level);
        if (at % 2 === 1) {
            path.push({ side: "right", hash: bytesToHex(level[at]) });
        }
        else {
            path.push({ side: "left", hash: bytesToHex(level[at - 2]) });
        }
        level = await fold(level, sha256);
        at = Math.floor((at + 1) / 2);
    }
    return path;
}
/**
 * Climb from one leaf to the root it claims. What a verifier does with a path.
 *
 * A path always has at least one step: a tree of one leaf is padded to two, so
 * even that leaf's path names its own twin as the sibling on the right. An
 * empty path is therefore never a proof of anything and is refused rather than
 * read as "the leaf is the root".
 */
export async function rootFromPath(leaf, path, sha256) {
    if (path.length === 0)
        throw new Error("a path has at least one step");
    let current = leaf;
    for (const step of path) {
        const sibling = hexToBytes(step.hash);
        current =
            step.side === "right"
                ? await sha256(concatBytes(current, sibling))
                : await sha256(concatBytes(sibling, current));
    }
    return current;
}
//# sourceMappingURL=merkle.js.map